<template>
  <div class="flex flex-col min-h-screen items-center">
    <div class="flex justify-between items-center w-full max-w-[1280px]">
      <div class="ml-4 sm:ml-0">
        <LogoIcon />
      </div>
      <div class="h-12 mt-5 mb-5 mr-4 sm:mr-8">
        <LanguageSwitch />
      </div>
    </div>
    <div class="max-w-[460px] w-full text-center flex-grow px-4">
      <h2 class="mt-5 mt-10 mb-6 text-center">
        {{ $t('messages.choose_auth_method.title') }}
      </h2>

      <!-- Passkey Card -->
      <div
        class="border-2 rounded-lg mb-4 cursor-pointer transition-all"
        :class="{
          'border-zyxel-green-700 bg-green-50': selectedMethod === 'passkey',
          'border-gray-200 bg-white': selectedMethod !== 'passkey',
          'opacity-50 cursor-not-allowed': isLocked && selectedMethod !== 'passkey',
          'p-6': selectedMethod === 'passkey',
          'p-4': selectedMethod !== 'passkey'
        }"
        role="button"
        tabindex="0"
        @click="selectMethod('passkey')"
        @keyup.enter="selectMethod('passkey')"
        @keyup.space="selectMethod('passkey')"
      >
        <!-- 標題行：Passkey 與打勾在同一行 -->
        <div
          class="flex justify-between items-center"
          :class="{ 'mb-4': selectedMethod === 'passkey' }"
        >
          <h3 class="text-lg font-bold">
            {{ $t('messages.menu.passkey') }}
          </h3>
          <!-- 只在選中時顯示勾選圖示 -->
          <div
            v-if="selectedMethod === 'passkey'"
            class="w-8 h-8 flex items-center justify-center flex-shrink-0"
          >
            <img
              src="@/assets/svg/icon_48px_tickCircle_Green.svg"
              alt="selected"
              class="w-full h-full"
            >
          </div>
        </div>

        <!-- 描述與連結內容 -->
        <div
          v-if="selectedMethod === 'passkey'"
          class="text-left"
        >
          <p class="text-sm text-zyxel-gray-800 mb-2">
            {{ $t('messages.choose_auth_method.passkey_description') }}
          </p>
          <a
            href="#"
            class="text-zyxel-green-700 font-bold hover:underline"
            @click.prevent.stop="handleAddPasskey"
          >
            {{ $t('messages.passkey.add_button') }}
          </a>
          <!-- Account Recovery 流程的 Passkey 上限說明 -->
          <span
            v-if="isResetPasswordMode"
            class="b3 block text-left flex mt-2"
          >
            <img
              class="h-3 mt-1 mr-1"
              src="@/assets/svg/icon_24px_info.svg"
              alt=""
            >
            {{ $t('messages.choose_auth_method.passkey_limit_notice') }}
          </span>
        </div>
      </div>
      <!-- Password Card -->
      <div
        class="border-2 rounded-lg mb-4 cursor-pointer transition-all"
        :class="{
          'border-zyxel-green-700 bg-green-50': selectedMethod === 'password',
          'border-gray-200 bg-white': selectedMethod !== 'password',
          'opacity-50 cursor-not-allowed': isLocked && selectedMethod !== 'password',
          'p-6': selectedMethod === 'password',
          'p-4': selectedMethod !== 'password'
        }"
        role="button"
        tabindex="0"
        @click="selectMethod('password')"
        @keyup.enter="selectMethod('password')"
        @keyup.space="selectMethod('password')"
      >
        <!-- 標題行：Password 與打勾在同一行 -->
        <div
          class="flex justify-between items-center"
          :class="{ 'mb-4': selectedMethod === 'password' }"
        >
          <h3 class="text-lg font-bold">
            {{ $t('messages.text_input.password') }}
          </h3>
          <!-- 只在選中時顯示勾選圖示 -->
          <div
            v-if="selectedMethod === 'password'"
            class="w-8 h-8 flex items-center justify-center flex-shrink-0"
          >
            <img
              src="@/assets/svg/icon_48px_tickCircle_Green.svg"
              alt="selected"
              class="w-full h-full"
            >
          </div>
        </div>

        <!-- 表單內容：填滿整個卡片寬度 -->
        <div v-if="selectedMethod === 'password'">
          <PasswordInput
            id="password"
            v-model="password"
            :class="{ 'error-input': errors.password }"
            @keyup.enter="handleSetPassword"
          />
          <span
            v-if="errors.password"
            class="error-message mt-1 b2 block text-left w-full"
          >
            {{ $t(errors_msg.password) }}
          </span>
          <span class="b3 block text-left flex mt-2">
            <img
              class="h-3 mt-1 mr-1"
              src="@/assets/svg/icon_24px_info.svg"
              alt=""
            >
            {{ $t('messages.text_input.password_info', { special_words: '!@#$%^&*()_+'}) }}
          </span>
          <button
            class="button-primary w-full mt-4"
            @click="handleSetPassword"
          >
            {{ isResetPasswordMode ? $t('messages.button.confirm') : $t('messages.button.confirm-n-sign_in') }}
          </button>
        </div>
      </div>

      <div class="mt-6 mb-6">
        <span
          class="mr-4 b1 text-zyxel-gray-800"
          style="color: #575757"
        >
          {{ $t('messages.common.hint_already_user') }}
        </span>
        <router-link
          class="b1 text-zyxel-green-700"
          :to="{ name: 'SignIn' }"
        >
          {{ $t('messages.common.link_sign_in') }}
        </router-link>
      </div>

      <div class="mb-6">
        <span
          class="mr-4 b1"
          style="color: #575757"
        >
          {{ $t('messages.common.hint_new_user') }}
        </span>
        <router-link
          class="b1 text-zyxel-green-700"
          :to="{ name: 'SignUp' }"
        >
          {{ $t('messages.common.link_create_account') }}
        </router-link>
      </div>
    </div>

    <!-- Dialogs -->
    <SetPasskeyNameDialog
      :is-set-passkey-open="isSetPasskeyNameDialogOpen"
      :passkey="newPasskeyData"
      @save="handleSetPasskeyName"
    />

    <PasskeySuccessDialog
      :is-show="showPasskeySuccessDialog"
      @sign-in="handlePasskeySignIn"
    />

    <AccountRecoverySuccessDialog
      :is-show="showAccountRecoverySuccessDialog"
      @confirm="handleRecoverySignIn"
    />

    <Enable2FAModal
      :show-modal="showEnableModal"
      @skip="handleSkip2FA"
      @remind="handleRemindMe"
      @enable="handleEnable2FA"
    />

    <TwoFactorAuthModal
      v-if="Object.keys(otp).length > 0"
      :show-modal="show2FAModal"
      :user="user"
      :otp="otp"
      @update-otp-backup-codes="otp.otp_backup_codes = $event"
      @close-modal="handleClose2FAModal"
    />

    <GeneralDialog
      :is-show="isGeneralDialogShow"
      :title="generalDialogProps.title"
      :description="generalDialogProps.description"
      @close="closeGeneralDialog"
    />

    <ZyxelFooter />
  </div>
</template>

<script>
import LogoIcon from '@/components/LogoIcon.vue';
import LanguageSwitch from "@/components/LanguageSwitch.vue";
import PasswordInput from '@/components/PasswordInput.vue';
import SetPasskeyNameDialog from '@/components/SetPasskeyNameDialog.vue';
import PasskeySuccessDialog from '@/components/PasskeySuccessDialog.vue';
import AccountRecoverySuccessDialog from '@/components/AccountRecoverySuccessDialog.vue';
import GeneralDialog from '@/components/GeneralDialog.vue';
import Enable2FAModal from "@/components/Enable2FAModal.vue";
import TwoFactorAuthModal from '@/components/TwoFactorAuthModal.vue';
import ZyxelFooter from "@/components/ZyxelFooter.vue";
import axios from 'axios';
import { apiBaseUrl } from '@/config';
import { useAuthStore } from '@/stores/auth';
import { base64urlDecode, arrayBufferToBase64url } from '@/utils/passkeyUtils';
import { logger } from '@/utils/logger';

export default {
  name: 'ChooseAuthMethod',
  components: {
    LogoIcon,
    LanguageSwitch,
    PasswordInput,
    SetPasskeyNameDialog,
    PasskeySuccessDialog,
    AccountRecoverySuccessDialog,
    GeneralDialog,
    Enable2FAModal,
    TwoFactorAuthModal,
    ZyxelFooter
  },
  data() {
    return {
      selectedMethod: 'passkey', // 預設選擇 Passkey
      isLocked: false, // UI 鎖定標記（防止競爭條件）
      password: '',
      errors: {
        password: false,
      },
      errors_msg: {
        password: ""
      },
      isSetPasskeyNameDialogOpen: false,
      newPasskeyData: {},
      showPasskeySuccessDialog: false,
      showAccountRecoverySuccessDialog: false,
      accessToken: null,
      showEnableModal: false,
      show2FAModal: false,
      otp: {},
      isGeneralDialogShow: false,
      generalDialogProps: {
        title: "",
        description: ""
      }
    };
  },
  computed: {
    user() {
      const authStore = useAuthStore();
      return authStore.current_user || {
        first_name: 'N/A',
        last_name: 'N/A',
        email: 'no-email@example.com',
        country: 'TW',
        contactPerson: 'None'
      };
    },
    isResetPasswordMode() {
      return this.$route.query.pre === 'resetpwd';
    }
  },
  methods: {
    selectMethod(method) {
      if (this.isLocked) return; // 鎖定時不允許切換
      this.selectedMethod = method;

      // 清除錯誤訊息
      if (method === 'passkey') {
        this.errors.password = false;
        this.password = '';
      }
    },

    async handleAddPasskey() {
      this.isLocked = true; // 鎖定 UI
      const token = this.$route.query.token;
      const tokenType = this.isResetPasswordMode ? 'reset_password' : 'confirmation';

      try {
        // 1. 呼叫 init API
        const initResponse = await axios.get(`${apiBaseUrl}/api/passkey/register/init`, {
          params: {
            token: token,
            token_type: tokenType
          }
        });

        const options = initResponse.data;

        // 2. 處理 WebAuthn options（Base64 解碼）
        options.user.id = base64urlDecode(options.user.id);
        options.challenge = base64urlDecode(options.challenge);
        if (options.excludeCredentials) {
          options.excludeCredentials.forEach(cred => {
            cred.id = base64urlDecode(cred.id);
          });
        }

        // Remove empty authenticatorAttachment
        if (options.authenticatorSelection?.authenticatorAttachment === "") {
          delete options.authenticatorSelection.authenticatorAttachment;
        }

        options.attestation = "direct";

        // 3. 觸發 WebAuthn（瀏覽器原生 API）
        const credential = await navigator.credentials.create({ publicKey: options });

        if (!credential || !credential.id) {
          logger.error('Invalid credential:', credential);
          this.isLocked = false;
          return;
        }

        // 4. 準備驗證 payload
        const verificationPayload = {
          id: credential.id,
          rawId: arrayBufferToBase64url(credential.rawId),
          response: {
            attestationObject: arrayBufferToBase64url(credential.response.attestationObject),
            clientDataJSON: arrayBufferToBase64url(credential.response.clientDataJSON),
            transports: credential.response.getTransports?.() ?? []
          },
          type: credential.type,
          clientExtensionResults: credential.clientExtensionResults ?? {},
          authenticatorAttachment: credential.authenticatorAttachment,
        };

        const verify_time = new Date().toString();

        // 5. 呼叫 verify API
        const verifyResponse = await axios.post(`${apiBaseUrl}/api/passkey/register/verify`, {
          token: token,
          token_type: tokenType,
          credential: verificationPayload,
          verify_time: verify_time
        });

        // 6. 根據模式處理結果
        if (this.isResetPasswordMode) {
          // Reset password 模式：不顯示命名 Dialog，直接顯示成功
          this.showAccountRecoverySuccessDialog = true;
        } else {
          // Activation 模式：儲存 JWT 並顯示命名 Dialog
          this.accessToken = verifyResponse.data.access_token;
          this.newPasskeyData = {
            passkey_id: verifyResponse.data.passkey_id,
            display_name: verifyResponse.data.display_name || 'My Passkey'
          };
          this.isSetPasskeyNameDialogOpen = true;
        }

      } catch (error) {
        this.isLocked = false; // 失敗後解鎖
        logger.error('Error in passkey registration:', error);

        if (error.name === 'NotAllowedError') {
          // User cancelled WebAuthn prompt
          this.showAddCancelledDialog();
        } else {
          const error_code = error?.response?.data?.detail?.error_code;
          if (error_code === 94522) {
            // Device no UV
            this.showAddFailNoUVDialog();
          } else {
            // General passkey failure
            this.showAddFailDialog();
          }
        }
      }
    },

    async handleSetPasskeyName(updatedPasskey) {
      try {
        // 1. 使用 JWT 認證更新 Passkey 名稱
        await axios.patch(
          `${apiBaseUrl}/api/passkey/${updatedPasskey.passkey_id}`,
          {
            display_name: updatedPasskey.display_name
          },
          {
            headers: {
              Authorization: `Bearer ${this.accessToken}`
            }
          }
        );

        // 2. 關閉命名 Dialog
        this.isSetPasskeyNameDialogOpen = false;
        this.newPasskeyData = {};

        // 3. 顯示成功 Dialog（Image #6）
        this.showPasskeySuccessDialog = true;

      } catch (error) {
        logger.error('Error updating passkey name:', error);
        alert(this.$t('messages.error_msg.update_passkey_failed'));
        this.isLocked = false;
      }
    },

    async handlePasskeySignIn() {
      // 使用 JWT 執行登入流程
      const auth = useAuthStore();
      await auth.authenticateUser(this.accessToken);

      // Passkey 本身就是 2FA (生物辨識 + 裝置持有)，直接完成登入
      this.showPasskeySuccessDialog = false;
      auth.getAuthorizationCodeAndRedirect();
    },

    async handleSetPassword() {
      if (!this.validatePassword(this.password)) {
        this.errors.password = true;
        this.errors_msg.password = 'messages.error_msg.password_complexity';
        return;
      }

      this.isLocked = true; // 鎖定 UI
      this.errors.password = false;
      const token = this.$route.query.token;

      try {
        const login_time = new Date().toString();
        const response = await axios.post(`${apiBaseUrl}/api/users/set_password`, {
          password: this.password,
          pre: this.$route.query.pre,
          token: token,
          login_time: login_time
        });

        if (this.isResetPasswordMode) {
          // Reset password 模式：顯示成功 Dialog（不登入）
          this.showAccountRecoverySuccessDialog = true;
        } else {
          // Activation 模式：自動登入
          const auth = useAuthStore();
          await auth.authenticateUser(response.data.access_token);

          if (auth.current_user && auth.current_user.setup_2fa === 0) {
            this.showEnableModal = true;
          } else {
            auth.getAuthorizationCodeAndRedirect();
          }
        }

      } catch (error) {
        this.isLocked = false; // 失敗後解鎖
        logger.error('Error setting password:', error);
        this.errors.password = true;
        this.errors_msg.password = "messages.error_msg.no_longer_valid";
      }
    },

    validatePassword(password) {
      const re = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z\d]).{8,}$/;
      return re.test(password);
    },

    async handleSkip2FA() {
      const auth = useAuthStore();
      await auth.update2FAStatus("2");
      this.showEnableModal = false;
      auth.getAuthorizationCodeAndRedirect();
    },

    async handleRemindMe() {
      const auth = useAuthStore();
      await auth.update2FAStatus("3");
      this.showEnableModal = false;
      auth.getAuthorizationCodeAndRedirect();
    },

    async handleEnable2FA() {
      const auth = useAuthStore();
      this.showEnableModal = false;
      const otpSettings = await auth.getOtpSettings();
      this.otp = otpSettings.otp;
      this.show2FAModal = true;
    },

    handleClose2FAModal() {
      const auth = useAuthStore();
      this.show2FAModal = false;
      auth.getAuthorizationCodeAndRedirect();
    },

    showAddFailDialog() {
      this.generalDialogProps.title = this.$t('messages.add_passkey_fail_modal.title');
      this.generalDialogProps.description = this.$t('messages.add_passkey_fail_modal.description');
      this.isGeneralDialogShow = true;
    },

    showAddFailNoUVDialog() {
      this.generalDialogProps.title = this.$t('messages.add_passkey_fail_modal.title');
      this.generalDialogProps.description = this.$t('messages.error_msg.device_no_uv');
      this.isGeneralDialogShow = true;
    },

    showAddCancelledDialog() {
      this.generalDialogProps.title = this.$t('messages.add_passkey_fail_modal.title');
      this.generalDialogProps.description = this.$t('messages.error_msg.passkey_cancelled');
      this.isGeneralDialogShow = true;
    },

    closeGeneralDialog() {
      this.isGeneralDialogShow = false;
    },

    handleRecoverySignIn() {
      this.showAccountRecoverySuccessDialog = false;

      // 清除 redirectAppId（如果有的話），避免影響後續登入
      const appid = localStorage.getItem('redirectAppId');
      if (appid) {
        localStorage.removeItem('redirectAppId');
      }

      this.$router.push({ name: 'SignIn' });
    }
  },
}
</script>